import { 
  User, 
  RegisterUserRequest,
  PasswordRegisterRequest,
  UnifiedLoginRequest,
  UnifiedRegisterRequest,
  UserProfileResponse,
  ApproveUserRequest,
  RejectUserRequest,
  DeleteUserRequest,
  UpdateProfileRequest,
  UpdateLocaleRequest,
  AddLicensePlateRequest,
  RemoveLicensePlateRequest,
  ChangePasswordRequest,
  ProfileResponse
} from '../types';
import { apiService } from './apiService';

export interface AuthService {
  // Telegram authentication
  login(initData: string): Promise<{ token: string; user: User }>;
  
  // Password authentication (new)
  loginWithPassword(username: string, password: string): Promise<{ token: string; user: User }>;
  registerWithPassword(userData: PasswordRegisterRequest): Promise<{ token: string; user: User }>;
  
  // Unified authentication (auto-detection)
  unifiedLogin(request: UnifiedLoginRequest): Promise<{ token: string; user: User }>;
  unifiedRegister(request: UnifiedRegisterRequest): Promise<{ token: string; user: User }>;
  
  logout(): void;
  
  // User management
  register(userData: RegisterUserRequest): Promise<User>;
  getUserProfile(): Promise<UserProfileResponse>;
  getMe(): Promise<User>;
  validateToken(): Promise<{ user: User; message: string }>;
  
  // Admin methods
  getPendingUsers(): Promise<User[]>;
  getAllUsers(): Promise<User[]>;
  approveUser(userId: string, request: ApproveUserRequest): Promise<void>;
  rejectUser(userId: string, request: RejectUserRequest): Promise<void>;
  deleteUser(userId: string, request: DeleteUserRequest): Promise<void>;
  
  // Profile Management methods
  getProfile(): Promise<ProfileResponse>;
  updateProfile(request: UpdateProfileRequest): Promise<void>;
  updateLocale(request: UpdateLocaleRequest): Promise<void>;
  addLicensePlate(request: AddLicensePlateRequest): Promise<void>;
  removeLicensePlate(request: RemoveLicensePlateRequest): Promise<void>;
  changePassword(request: ChangePasswordRequest): Promise<void>;
  
  // Token management
  getToken(): string | null;
  setToken(token: string): void;
  clearToken(): void;
  isAuthenticated(): boolean;
  isKioskUser(): boolean;
  
  // Kiosk mode impersonation
  impersonate(telegramId: number): Promise<{ token: string; user: User }>;
  
  // Kiosk mode token management
  backupCurrentToken(): string | null;
  restoreToken(backupToken: string | null): void;
  temporarilySetToken(tempToken: string): void;
  
  // Kiosk mode workflow helpers
  executeAsImpersonatedUser<T>(impersonationToken: string, operation: () => Promise<T>): Promise<T>;
  
  // Kiosk mode cleanup
  clearKioskImpersonationData(): void;
  ensureKioskMode(): boolean;
}

class AuthServiceImpl implements AuthService {
  private tokenKey = 'authToken';

  async login(telegramInitData: string): Promise<{ token: string; user: User }> {
    try {
      // Send Telegram init data to backend for verification
      const response = await apiService.post<{ user: User; token: string }>('/auth/telegram', {
        initData: telegramInitData,
      });

      // Store auth token
      this.setToken(response.token);
      apiService.setAuthToken(response.token);

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Authentication failed');
    }
  }

  async loginWithPassword(username: string, password: string): Promise<{ token: string; user: User }> {
    try {
      // Send username/password to unified auth endpoint
      const response = await apiService.post<{ user: User; token: string }>('/auth/login', {
        username,
        password
      });

      // Store auth token
      this.setToken(response.token);
      apiService.setAuthToken(response.token);

      return response;
    } catch (error) {
      console.error('Password login failed:', error);
      throw new Error('Authentication failed');
    }
  }

  async registerWithPassword(userData: PasswordRegisterRequest): Promise<{ token: string; user: User }> {
    try {
      // Send password registration to unified auth endpoint
      const response = await apiService.post<{ user: User; token: string }>('/auth/register', {
        username: userData.username,
        password: userData.password,
        fullName: userData.fullName,
        phoneNumber: userData.phoneNumber,
        primaryPlate: userData.primaryPlate,
        locale: userData.locale || 'en'
      });

      // Store auth token
      this.setToken(response.token);
      apiService.setAuthToken(response.token);

      return response;
    } catch (error) {
      console.error('Password registration failed:', error);
      throw new Error('Registration failed');
    }
  }

  async unifiedLogin(request: UnifiedLoginRequest): Promise<{ token: string; user: User }> {
    try {
      // Use the unified login endpoint that auto-detects authentication type
      const response = await apiService.post<{ user: User; token: string }>('/auth/login', request);

      // Store auth token
      this.setToken(response.token);
      apiService.setAuthToken(response.token);

      return response;
    } catch (error) {
      console.error('Unified login failed:', error);
      throw new Error('Authentication failed');
    }
  }

  async unifiedRegister(request: UnifiedRegisterRequest): Promise<{ token: string; user: User }> {
    try {
      // Use the unified register endpoint that auto-detects authentication type
      const response = await apiService.post<{ user: User; token: string }>('/auth/register', request);

      // Store auth token
      this.setToken(response.token);
      apiService.setAuthToken(response.token);

      return response;
    } catch (error) {
      console.error('Unified registration failed:', error);
      throw new Error('Registration failed');
    }
  }

  logout(): void {
    this.clearToken();
    apiService.clearAuthToken();
  }

  async register(userData: RegisterUserRequest): Promise<User> {
    return apiService.post<User>('/user/register', userData);
  }

  async getUserProfile(): Promise<UserProfileResponse> {
    return apiService.get<UserProfileResponse>('/auth/profile');
  }

  async getMe(): Promise<User> {
    return apiService.get<User>('/me');
  }

  async validateToken(): Promise<{ user: User; message: string }> {
    return apiService.get<{ user: User; message: string }>('/auth/validate');
  }

  async getPendingUsers(): Promise<User[]> {
    const response = await apiService.get<{ users: User[] }>('/admin/users/pending');
    return response.users;
  }

  async getAllUsers(): Promise<User[]> {
    const response = await apiService.get<{ users: User[] }>('/admin/users');
    return response.users;
  }

  async approveUser(userId: string, request: ApproveUserRequest): Promise<void> {
    await apiService.post(`/admin/users/${userId}/approve`, request);
  }

  async rejectUser(userId: string, request: RejectUserRequest): Promise<void> {
    await apiService.post(`/admin/users/${userId}/reject`, request);
  }

  async deleteUser(userId: string, request: DeleteUserRequest): Promise<void> {
    await apiService.post(`/admin/users/${userId}/delete`, request);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // For development, allow test tokens
    if (token.includes('test-signature-for-development-only')) {
      try {
        const payload = this.parseJWTTestToken(token);
        // Check if token is expired
        if (payload.exp && Date.now() >= payload.exp * 1000) {
          this.clearToken();
          return false;
        }
        return true;
      } catch {
        this.clearToken();
        return false;
      }
    }
    
    // For real tokens, just check if they exist (backend will validate)
    return !!token;
  }

  /**
   * Parse test JWT token payload for development
   */
  private parseJWTTestToken(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    // Decode base64 payload
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * Get user information from test token
   */
  getUserInfo(): any {
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) return null;
    
    // For test tokens
    if (token.includes('test-signature-for-development-only')) {
      try {
        return this.parseJWTTestToken(token);
      } catch {
        return null;
      }
    }
    
    // For real tokens, we don't parse them client-side
    return null;
  }

  /**
   * Get Telegram user ID from token
   */
  getTelegramId(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.telegram_id || null;
  }

  /**
   * Check if the current user is a Kiosk user based on JWT claims
   * NOTE: Admin users NEVER enter kiosk mode - admin role overrides kiosk flag
   */
  isKioskUser(): boolean {
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) return false;
    
    try {
      // For test tokens
      if (token.includes('test-signature-for-development-only')) {
        const payload = this.parseJWTTestToken(token);
        // Admin users (role === 2) never enter kiosk mode
        if (payload.role === 2) return false;
        return payload.is_kiosk === true || payload.is_kiosk === 'true';
      }
      
      // For real JWT tokens, parse the payload
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(decoded);
      
      // Admin users (role === 2) never enter kiosk mode
      if (claims.role === 2) return false;
      
      return claims.is_kiosk === true || claims.is_kiosk === 'true';
    } catch (error) {
      console.warn('Failed to parse JWT token for Kiosk check:', error);
      return false;
    }
  }

  /**
   * Generate a test JWT token for development purposes
   * This creates a token that matches the backend's expected format
   */
  generateTestToken(telegramId: string = '123456789', userName: string = 'Test User', isKiosk: boolean = false): string {
    // Create a test JWT payload that matches the backend's expectations
    const payload = {
      sub: `test-user-${telegramId}`, // Subject
      iss: 'chargequeue', // Issuer (matches backend config)
      aud: 'telegram-webapp', // Audience (matches backend config)
      telegram_id: telegramId,
      user_id: `test-user-${telegramId}`,
      name: userName,
      email: `test-${telegramId}@example.com`,
      is_kiosk: isKiosk, // Add Kiosk claim for testing
      iat: Math.floor(Date.now() / 1000), // Issued at
      exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60), // Expires in 24 hours
      scope: 'webapp-access'
    };

    // Encode payload (simple base64 for testing - not secure but valid format!)
    const encodedPayload = btoa(JSON.stringify(payload))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    // Create a mock JWT structure (header.payload.signature)
    const header = {
      alg: 'HS256',
      typ: 'JWT'
    };
    
    const encodedHeader = btoa(JSON.stringify(header))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    // For development, create a signature that the backend might recognize as test
    const signature = 'test-signature-for-development-only';
    
    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  /**
   * Set a test authentication token for development by calling the backend
   */
  async setTestAuthentication(telegramId?: string, userName?: string): Promise<boolean> {
    try {
      const testTokenRequest = {
        telegramId: telegramId ? parseInt(telegramId) : 123456789,
        firstName: userName || 'Test',
        lastName: 'User',
        username: 'testuser'
      };

      const response = await apiService.post<{
        token: string;
        user: any;
        message: string;
      }>('/auth/test-token', testTokenRequest);

      if (response.token) {
        this.setToken(response.token);
        console.log('🔑 Test authentication token set from backend:', {
          telegramId: testTokenRequest.telegramId,
          userName: testTokenRequest.firstName,
          message: response.message
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Failed to set test authentication:', error);
      return false;
    }
  }

  // Profile Management methods
  async getProfile(): Promise<ProfileResponse> {
    return await apiService.get<ProfileResponse>('/profile');
  }

  async updateProfile(request: UpdateProfileRequest): Promise<void> {
    await apiService.put('/profile', request);
  }

  async updateLocale(request: UpdateLocaleRequest): Promise<void> {
    await apiService.put('/profile/locale', request);
  }

  async addLicensePlate(request: AddLicensePlateRequest): Promise<void> {
    await apiService.post('/profile/license-plates', request);
  }

  async removeLicensePlate(request: RemoveLicensePlateRequest): Promise<void> {
    await apiService.delete(`/profile/license-plates?licensePlate=${encodeURIComponent(request.licensePlate)}`);
  }

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await apiService.post('/profile/change-password', request);
  }

  async impersonate(telegramId: number): Promise<{ token: string; user: User }> {
    try {
      const response = await apiService.post<{ token: string; user: User; message: string }>('/auth/impersonate', {
        telegramId: telegramId
      });

      // DON'T store the token - just return it to caller
      // The caller will handle token management for temporary operations
      return {
        token: response.token,
        user: response.user
      };
    } catch (error) {
      console.error('Impersonation failed:', error);
      throw error;
    }
  }

  backupCurrentToken(): string | null {
    return this.getToken();
  }

  restoreToken(backupToken: string | null): void {
    if (backupToken) {
      this.setToken(backupToken);
      apiService.setAuthToken(backupToken);
    } else {
      this.clearToken();
      apiService.clearAuthToken();
    }
  }

  temporarilySetToken(tempToken: string): void {
    // Temporarily set token for API calls without persisting to storage
    apiService.setAuthToken(tempToken);
  }

  async executeAsImpersonatedUser<T>(impersonationToken: string, operation: () => Promise<T>): Promise<T> {
    const originalToken = this.backupCurrentToken();
    
    try {
      this.temporarilySetToken(impersonationToken);
      const result = await operation();
      return result;
    } finally {
      this.restoreToken(originalToken);
    }
  }

  clearKioskImpersonationData(): void {
    // Clear any temporary impersonation data from window
    if (typeof window !== 'undefined' && window.__kioskImpersonationData) {
      delete window.__kioskImpersonationData;
      console.log('Kiosk mode: Cleared impersonation data');
    }
  }

  ensureKioskMode(): boolean {
    // Check if current user is a kiosk user
    const isKiosk = this.isKioskUser();
    
    if (!isKiosk) {
      console.warn('Current user is not a kiosk user - cannot ensure kiosk mode');
      return false;
    }

    // Clear any impersonation data
    this.clearKioskImpersonationData();
    
    // Verify we still have a valid kiosk token
    const currentToken = this.getToken();
    if (!currentToken) {
      console.error('No authentication token found - kiosk session may be expired');
      return false;
    }

    console.log('Kiosk mode: Ensured clean kiosk state');
    return true;
  }
}

export const authService = new AuthServiceImpl();